import React, { useState, useEffect } from 'react';

const AllocationSlider = ({ value: externalValue, onChange }) => {
  // Local state for immediate updates
  const [localValue, setLocalValue] = useState(externalValue);
  const [isSliding, setIsSliding] = useState(false);

  // Update local value when external value changes
  useEffect(() => {
    if (!isSliding) {
      setLocalValue(externalValue);
    }
  }, [externalValue, isSliding]);

  // Handle local changes during sliding
  const handleChange = (e) => {
    const newValue = parseInt(e.target.value);
    setLocalValue(newValue);
  };

  // Handle slide start
  const handleSlideStart = () => {
    setIsSliding(true);
  };

  // Handle slide end
  const handleSlideEnd = () => {
    setIsSliding(false);
    onChange(localValue);
  };

  return (
    <div className="w-full max-w-md p-4">
      {/* Gradient Background Container */}
      <div className="bg-gradient-to-r from-purple-200 to-indigo-200 rounded-xl p-6 shadow-sm">
        {/* Allocation Display */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          {/* Equity Display */}
          <div className="flex flex-col items-center md:items-start space-y-1">
            <span className="text-sm text-gray-600">Equity</span>
            <div className="text-2xl font-semibold text-gray-800">{localValue}%</div>
          </div>

          {/* Divider (Visible on Medium and Larger Screens) */}
          <div className="hidden md:block h-8 w-px bg-gray-300 opacity-50 mx-4" />

          {/* Fixed Display */}
          <div className="flex flex-col items-center md:items-end space-y-1 mt-4 md:mt-0">
            <span className="text-sm text-gray-600">Fixed</span>
            <div className="text-2xl font-semibold text-gray-800">{100 - localValue}%</div>
          </div>
        </div>
      </div>

      {/* Slider Section */}
      <div className="mt-6">
        {/* Slider Wrapper */}
        <div className="bg-white rounded-lg px-2 shadow-md">
          {/* Slider Track */}
          <input
            type="range"
            min="0"
            max="100"
            value={localValue}
            onChange={handleChange}
            onMouseDown={handleSlideStart}
            onTouchStart={handleSlideStart}
            onMouseUp={handleSlideEnd}
            onTouchEnd={handleSlideEnd}
            className="w-full bg-gradient-to-r from-blue-400 to-emerald-50 rounded-lg appearance-none cursor-pointer focus:outline-none"
            style={{
              background: `linear-gradient(to right, #a78bfa ${localValue}%, #c7d2fe ${localValue}%)`,
            }}
            aria-label="Allocation Slider"
          />
        </div>

        {/* Tick Marks */}
        <div className="grid grid-cols-5 gap-0 mt-3 text-xs text-gray-500">
          {[0, 25, 50, 75, 100].map((tick) => (
            <div key={tick} className="text-center">
              {tick}%
            </div>
          ))}
        </div>
      </div>

      {/* Custom Slider Thumb Styling */}
      <style jsx>{`
        input[type='range']::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #ffffff;
          border: 2px solid #8b5cf6; /* Tailwind purple-600 */
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
          margin-top: -7px; /* Aligns the thumb vertically */
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[type='range']::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        input[type='range']::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #ffffff;
          border: 2px solid #8b5cf6; /* Tailwind purple-600 */
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[type='range']::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        input[type='range']::-ms-thumb {
          width: 16px;
          height: 16px;
          background: #ffffff;
          border: 2px solid #8b5cf6; /* Tailwind purple-600 */
          border-radius: 50%;
          cursor: pointer;
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[type='range']::-ms-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        /* Remove default focus styles and add custom focus */
        input[type='range']:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.3); /* Tailwind purple-400 with opacity */
        }
      `}</style>
    </div>
  );
};

export default AllocationSlider;